import { PDFDocument, PDFFont, PDFPage, rgb, StandardFonts } from "pdf-lib";
import { Country, Shipment } from "../types";

/**
 * Air Waybill (AWB) PDF — air shipments only.
 *
 * Box layout follows the same approach as the BOL generator. Only fields backed
 * by real data are rendered; doc-only fields with no source are skipped. The
 * air block has no remarks rows (unlike sea), so the body is the flight +
 * MAWB/AWB data, parties, route, and cargo description/weight.
 */

function safe<T>(fn: () => T, fallback = "-"): string {
  try {
    const val = fn();
    return val !== undefined && val !== null && val !== ""
      ? String(val)
      : fallback;
  } catch {
    return fallback;
  }
}

function wrapText(
  text: string,
  font: PDFFont,
  size: number,
  maxWidth: number,
  maxLines = 6,
): string[] {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    } else {
      line = candidate;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  return lines;
}

export async function createAirWaybillPdf(
  data: Shipment,
  countries: Country[],
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.setTitle(
    `Air Waybill${data.referenceNumber ? ` - ${data.referenceNumber}` : ""}`,
  );
  pdfDoc.setAuthor("LiveCargo");
  pdfDoc.setSubject("Air Waybill");
  pdfDoc.setCreationDate(new Date());

  let page: PDFPage = pdfDoc.addPage([595, 842]); // A4
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const black = rgb(0.07, 0.07, 0.1);
  const gray = rgb(0.42, 0.45, 0.5);
  const border = rgb(0.12, 0.16, 0.22);

  const margin = 36;
  const innerWidth = width - 2 * margin;
  let yPos = height - margin;

  const countryOptions = (countries ?? []).flat().map((c) => ({
    value: c._id,
    label: c.name,
  }));
  const countryName = (codeOrName: any): string => {
    if (!codeOrName) return "";
    const code = typeof codeOrName === "string" ? codeOrName : codeOrName?._id;
    if (!code) return "";
    const found = countryOptions.find(
      (c) => c.value?.toLowerCase?.() === code.toLowerCase?.(),
    );
    return found?.label || (typeof codeOrName === "string" ? "" : "");
  };

  const air = data.airDetails;
  const awb = air?.awb;
  const chgs = (term?: string) =>
    term === "Prepaid"
      ? "PP (Prepaid)"
      : term === "Collect"
        ? "CC (Collect)"
        : "-";

  const partyLines = (party: any): string[] => {
    if (!party) return [];
    const addr = party.address ?? {};
    const cityLine = [addr.postalCode, addr.city, addr.state]
      .filter(Boolean)
      .join(" ");
    return [
      safe(() => addr.street, ""),
      cityLine,
      countryName(addr.country),
      party.registerationCode ? `Reg.nr ${party.registerationCode}` : "",
      safe(() => party.emailAddress, ""),
      safe(() => party.phoneNumber, ""),
    ].filter((l) => l && l !== "-");
  };

  const drawCell = (
    x: number,
    top: number,
    w: number,
    h: number,
    label: string,
    valueLines: string[],
    opts: { valueSize?: number; valueBold?: boolean } = {},
  ) => {
    page.drawRectangle({
      x,
      y: top - h,
      width: w,
      height: h,
      borderColor: border,
      borderWidth: 1,
    });
    page.drawText(label.toUpperCase(), {
      x: x + 6,
      y: top - 12,
      size: 7,
      font: boldFont,
      color: gray,
    });
    const size = opts.valueSize ?? 9;
    let vy = top - 24;
    for (const line of valueLines) {
      if (vy < top - h + 6) break;
      page.drawText(line, {
        x: x + 6,
        y: vy,
        size,
        font: opts.valueBold ? boldFont : font,
        color: black,
      });
      vy -= size + 3;
    }
  };

  const ensure = (needed: number) => {
    if (yPos - needed < margin) {
      page = pdfDoc.addPage([595, 842]);
      yPos = height - margin;
    }
  };

  // ── Title ───────────────────────────────────────────────────────────────────
  const title = "AIR WAYBILL";
  const titleW = boldFont.widthOfTextAtSize(title, 18);
  page.drawText(title, {
    x: (width - titleW) / 2,
    y: yPos - 6,
    size: 18,
    font: boldFont,
    color: black,
  });
  const sub = "Air Waybill · Non-negotiable";
  const subW = font.widthOfTextAtSize(sub, 8);
  page.drawText(sub, {
    x: (width - subW) / 2,
    y: yPos - 20,
    size: 8,
    font,
    color: gray,
  });
  yPos -= 36;

  const fmtDate = (d?: string) => (d ? safe(() => d.split("T")[0]) : "-");
  const halfW = innerWidth / 2;
  const thirdW = innerWidth / 3;
  const quarterW = innerWidth / 4;

  // ── Row 1 · AWB No. · Issuing carrier's agent name & city ───────────────────
  const metaH = 40;
  drawCell(
    margin,
    yPos,
    halfW,
    metaH,
    "Air Waybill No.",
    [safe(() => awb?.awbNumber || air?.forwarder?.hawbNumber)],
    { valueBold: true, valueSize: 10 },
  );
  drawCell(
    margin + halfW,
    yPos,
    halfW,
    metaH,
    "Issuing Carrier's Agent Name & City",
    [
      [
        safe(() => awb?.issuingAgentName || air?.forwarder?.companyName, ""),
        safe(() => awb?.issuingAgentCity, ""),
      ]
        .filter((v) => v && v !== "-")
        .join(", ") || "-",
    ],
  );
  yPos -= metaH;

  // ── Row 2 · Shipper · Consignee ─────────────────────────────────────────────
  const partyH = 96;
  drawCell(margin, yPos, halfW, partyH, "Shipper Name & Address", [
    safe(() => data.pickup?.companyName),
    ...partyLines(data.pickup),
  ]);
  drawCell(margin + halfW, yPos, halfW, partyH, "Consignee Name & Address", [
    safe(() => data.delivery?.companyName),
    ...partyLines(data.delivery),
  ]);
  yPos -= partyH;

  // ── Row 3 · Notify party · MAWB reference ───────────────────────────────────
  const notify = awb?.notifyParty;
  const notifyLines = [
    safe(() => notify?.companyName, ""),
    safe(() => notify?.contactPerson, ""),
    safe(() => notify?.email, ""),
    safe(() => notify?.phone, ""),
  ].filter((l) => l && l !== "-");
  const notifyH = Math.max(56, 24 + notifyLines.length * 12);
  ensure(notifyH);
  drawCell(
    margin,
    yPos,
    halfW,
    notifyH,
    "Notify Party",
    notifyLines.length ? notifyLines : ["-"],
  );
  drawCell(margin + halfW, yPos, halfW, notifyH, "MAWB Reference", [
    safe(() => air?.mawbNumber),
  ]);
  yPos -= notifyH;

  // ── Row 4 · Departure · By (carrier) / Flight · Destination ─────────────────
  const portH = 46;
  ensure(portH);
  drawCell(margin, yPos, thirdW, portH, "Airport of Departure", [
    safe(() => air?.airportOfDeparture),
  ]);
  drawCell(margin + thirdW, yPos, thirdW, portH, "By (Carrier) / Flight", [
    [safe(() => air?.airlineName, ""), safe(() => air?.flightNumber, "")]
      .filter((v) => v && v !== "-")
      .join(" / ") || "-",
    air?.etd ? fmtDate(air.etd) : "",
  ].filter(Boolean));
  drawCell(margin + thirdW * 2, yPos, thirdW, portH, "Airport of Destination", [
    safe(() => air?.airportOfArrival),
  ]);
  yPos -= portH;

  // ── Row 5 · Charges · Currency · CHGS · Declared values ─────────────────────
  const chargesH = 40;
  ensure(chargesH);
  drawCell(margin, yPos, quarterW, chargesH, "Currency", [
    safe(() => awb?.currency),
  ]);
  drawCell(margin + quarterW, yPos, quarterW, chargesH, "CHGS Code (Freight)", [
    chgs(awb?.freightPaymentTerms),
  ]);
  drawCell(margin + quarterW * 2, yPos, quarterW, chargesH, "Decl. Val. Carriage", [
    safe(() => awb?.declaredValueCarriage, "NVD"),
  ]);
  drawCell(margin + quarterW * 3, yPos, quarterW, chargesH, "Decl. Val. Customs", [
    safe(() => awb?.declaredValueCustoms, "NCV"),
  ]);
  yPos -= chargesH;

  // ── Row 6 · Cargo details · Description · Gross/Chargeable weight ────────────
  const descW = innerWidth * 0.66;
  const weightW = innerWidth - descW;

  const totalPackages = (data.cargoItems ?? []).reduce(
    (s, i) => s + (Number(i.quantity) || 0),
    0,
  );
  const totalWeight = (data.cargoItems ?? []).reduce(
    (s, i) => s + (Number(i.weightKg) || 0),
    0,
  );
  const totalVolume = (data.cargoItems ?? []).reduce(
    (s, i) => s + (Number(i.volumeM3) || 0),
    0,
  );
  const totalChargeable = (data.cargoItems ?? []).reduce(
    (s, i) => s + (Number(i.chargeableWeight) || 0),
    0,
  );

  const descLines: string[] = [];
  if (totalPackages > 0)
    descLines.push(`${totalPackages} package(s) said to contain:`);
  for (const item of data.cargoItems ?? []) {
    const desc = safe(() => item.description, "");
    const qtyType = [item.quantity, item.type].filter(Boolean).join(" ");
    const text = [qtyType, desc].filter((v) => v && v !== "-").join(" — ");
    if (text)
      wrapText(text, font, 9, descW - 14, 2).forEach((l, i) =>
        descLines.push(i === 0 ? `- ${l}` : `  ${l}`),
      );
  }

  const descH = Math.max(96, 30 + descLines.length * 12);
  ensure(descH);
  drawCell(
    margin,
    yPos,
    descW,
    descH,
    "No. & Kind of Packages · Description of Goods",
    descLines.length ? descLines : ["-"],
  );
  drawCell(margin + descW, yPos, weightW, descH, "Gross / Chargeable Weight", [
    totalWeight > 0 ? `${totalWeight.toLocaleString("en-US")} kg` : "-",
    totalChargeable > 0
      ? `Chg. ${totalChargeable.toLocaleString("en-US")} kg`
      : "",
    totalVolume > 0 ? `${totalVolume.toLocaleString("en-US")} m³` : "",
  ].filter(Boolean));
  yPos -= descH;

  // ── Row 7 · Other charges · Special handling codes ──────────────────────────
  const handlingH = 36;
  ensure(handlingH);
  drawCell(margin, yPos, halfW, handlingH, "Other Charges", [
    chgs(awb?.otherChargesPayment),
  ]);
  drawCell(margin + halfW, yPos, halfW, handlingH, "Special Handling Codes (SHC)", [
    (awb?.specialHandlingCodes ?? []).filter(Boolean).join(", ") || "-",
  ]);
  yPos -= handlingH;

  // ── Row 8 · Handling information / remarks (full width) ─────────────────────
  const remarkLines: string[] = [];
  for (const r of awb?.remarks ?? []) {
    if (r) wrapText(r, font, 9, innerWidth - 14, 3).forEach((l) => remarkLines.push(l));
  }
  const remarkH = Math.max(40, 24 + remarkLines.length * 12);
  ensure(remarkH);
  drawCell(
    margin,
    yPos,
    innerWidth,
    remarkH,
    "Handling Information / Remarks",
    remarkLines.length ? remarkLines : ["-"],
  );
  yPos -= remarkH;

  // ── Footer · Place of execution · Date · Signature ──────────────────────────
  const footH = 56;
  ensure(footH + 24);
  drawCell(margin, yPos, thirdW, footH, "Place of Execution", [
    safe(() => awb?.issuingAgentCity),
  ]);
  drawCell(margin + thirdW, yPos, thirdW, footH, "Date", [
    awb?.awbDate ? fmtDate(awb.awbDate) : fmtDate(air?.mawbDate),
  ]);
  drawCell(
    margin + thirdW * 2,
    yPos,
    thirdW,
    footH,
    "Signature (Issuing Carrier / Agent)",
    [safe(() => awb?.issuingAgentName || air?.forwarder?.companyName)],
  );
  yPos -= footH + 14;

  page.drawText("Generated by LiveCargo", {
    x: margin,
    y: 24,
    size: 8,
    font,
    color: gray,
  });

  return await pdfDoc.save();
}

export async function downloadAirWaybillPdf(
  data: Shipment,
  countries: Country[],
  filename?: string,
): Promise<void> {
  const pdfBytes = await createAirWaybillPdf(data, countries);
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filename ||
    `Air-Waybill${data.referenceNumber ? `-${data.referenceNumber}` : ""}.pdf`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
